import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import Modal from "@mui/material/Modal";
import BackButton from "../../../components/common/BackButton";
import SearchButton from "../../../components/common/SearchButton";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/common/loader";
import CentralMonitorError from "../../../pages/error/centralMonitorError";
import axios from "axios";
import urls from "../../../utils/url";
import Cookie from "js-cookie";
import { AlertAudio } from "./AlertAudio";

import GraphCard from "./GraphCard";
import "./CentralGraphLayout.scss";
import {
  deleteFromIndexedDB,
  cleanupOldIndexedDB,
  deleteFetalMarkers,
} from "../../../utils/IndexedDB";
import Alerts_Cms from "./Alerts/AlertsCms";
import AlertsModal from "./Alerts/AlertsModal";
import { performLogout } from "../../../utils/logout";
import { selectMergedKeyarData } from "../../../redux/features/central_monitor/liveKeyarData";

const CentralGraphLayout = ({ keyarData, isResyncing }) => {
  // console.log(keyarData)

  const navigate = useNavigate();

  const cookie = Cookie.get("access_token");
  const cookieData = Cookie.get("data");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [accountType, setAccountType] = useState("");
  const [theme, setTheme] = useState(() => {
    return Cookie.get("theme") || "dark-theme";
  });
  const monitoringActiveRef = useRef(false);
  const lastNonEmptyDataRef = useRef([]);
  const [activeAlerts, setActiveAlerts] = useState({});
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertsAnchorEl, setAlertsAnchorEl] = useState(null);

  const patientFirstSeenRef = useRef(new Map());

  const patientMapRef = useRef(new Map());
  // const MOCK_20_MIN_ROLLOVER = true;
  const patientSessionsRef = useRef(new Map());
  // session_uuid -> the card identity (patientID) this session was first
  // seen under. A session that starts unassigned (patient_id 0, keyed by its
  // session_uuid) and is later assigned a real patient keeps the same
  // session_uuid, so the card keeps its original identity instead of being
  // torn down and rebuilt when Redux re-keys the entry to the real ID.
  const sessionKeyRef = useRef(new Map());
  const patientLastSeenRef = useRef(new Map());
  const PATIENT_GRACE_PERIOD_MS = 20 * 1000;
  const patientNoDataSinceRef = useRef(new Map());
  const noPatientsSinceRef = useRef(null);
  const NO_PATIENTS_GRACE_MS = 10_000;
  const hasAlerts = Object.keys(activeAlerts).length > 0;
  const [activePatientID, setActivePatientID] = useState(() => {
    try {
      return sessionStorage.getItem("cms_active_patient_id") || null;
    } catch {
      return null;
    }
  });
const activePatient = data.find(
  (p) => String(p.patientID) === String(activePatientID)
);

  const isSingleView = !!activePatientID;
  // 👉 ADD THIS BLOCK
  const childPlaces = (() => {
    try {
      return JSON.parse(Cookie.get("child_places") || "[]");
    } catch {
      return [];
    }
  })();

  console.log("childplaces",childPlaces);

  const isSingleHospital = childPlaces.length === 1;

  const singleHospitalName = isSingleHospital ? childPlaces[0]?.name : "";
  const singleHospitalLogo = isSingleHospital ? childPlaces[0]?.logo : null;

  const ALERTS_STORAGE_KEY = "cms_active_alerts";
  const [alertsHydrated, setAlertsHydrated] = useState(false);
  const [skipPersistOnce, setSkipPersistOnce] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(AlertAudio.isUnlocked);

  const hasReceivedFirstDataRef = useRef(false);
  const prevStablePatientCountRef = useRef(0);
  const prevPatientIDsRef = useRef(new Set());
  const missingPatientsRef = useRef(new Map());
  const MISSING_CONFIRMATION_MS = 1000;
  
  // ⚠️ BUSINESS RULE: 12-patient license limit
  const MAX_PATIENTS_ALLOWED = 12;
  
  const lastPlayedAlertTsRef = useRef(0);
  const activeSortingAlertsRef = useRef(new Map());
  const [cleaningPatients, setCleaningPatients] = useState(new Set());
  const hardCleanedPatientsRef = useRef(new Set());

  const patientOrderTsRef = useRef(new Map());
  const justUnmutedRef = useRef(false);

  // useCallback: a stable reference here (instead of a fresh function every
  // render) is what lets GraphCard's React.memo actually skip re-rendering
  // unaffected patient cards — otherwise every card re-renders on every tick
  // regardless of whether that patient's own data changed.
  const handleAlertChange = useCallback((patientID, isCritical, meta = {}) => {
    if (!Array.isArray(meta.alerts) || meta.alerts.length === 0) return;

    const now = Date.now();

    if (isCritical) {
      activeSortingAlertsRef.current.set(patientID, now);
      patientOrderTsRef.current.set(patientID, now);
    } else {
      activeSortingAlertsRef.current.delete(patientID);
    }

    setActiveAlerts((prev) => {
      const existing = prev[patientID];
      const existingAlerts = existing?.alerts || [];

      const alertMap = new Map();

      existingAlerts.forEach((a) => {
        alertMap.set(a.param_type, a);
      });

      meta.alerts.forEach((a) => {
        alertMap.set(a.param_type, {
          ...a,
          param_type: a.param_type.toUpperCase(),
          timestamp: a.timestamp ?? Date.now(),
        });
      });
      const hasNewAlert = meta.alerts.some((a) => {
        return !existingAlerts.find(
          (ex) =>
            ex.param_type === a.param_type && ex.timestamp === a.timestamp,
        );
      });

      return {
        ...prev,
        [patientID]: {
          patientID,
          mrn: meta.mrn ? meta.mrn : null,
          name: meta.name ?? existing?.name,
          age: meta.age ?? existing?.age,
          seen: hasNewAlert ? false : (existing?.seen ?? false),
          alerts: Array.from(alertMap.values()).sort(
            (a, b) => b.timestamp - a.timestamp,
          ),
        },
      };
    });
  }, []);

  const cleanupPatientCompletely = (patientID) => {
    hardCleanedPatientsRef.current.add(patientID);

    // console.log(
    //   `[CLEANUP] Before delete | sessionLocked =`,
    //   patientSessionsRef.current.get(patientID),
    // );

    if (cleaningPatients.has(patientID)) return;
    // console.log(`🧹 Cleaning patient: ${patientID}`);

    setCleaningPatients((prev) => new Set(prev).add(patientID));
    setActiveAlerts((prev) => {
      if (!prev[patientID]) return prev;
      const updated = { ...prev };
      delete updated[patientID];
      return updated;
    });

    deleteFromIndexedDB(patientID);
    deleteFetalMarkers(patientID);
    try {
      localStorage.setItem(
        `patient_hard_cleaned_${patientID}`,
        Date.now().toString(),
      );
    } catch (e) {}

    try {
      const stored = JSON.parse(
        localStorage.getItem(ALERTS_STORAGE_KEY) || "{}",
      );
      if (stored[patientID]) {
        delete stored[patientID];
        localStorage.setItem(ALERTS_STORAGE_KEY, JSON.stringify(stored));
      }
    } catch (e) {}

    localStorage.removeItem(`patient_first_seen_${patientID}`);
    patientMapRef.current.delete(patientID);
    patientSessionsRef.current.delete(patientID);
    patientLastSeenRef.current.delete(patientID);
    patientNoDataSinceRef.current.delete(patientID);
    patientFirstSeenRef.current.delete(patientID);
    activeSortingAlertsRef.current.delete(patientID);
    missingPatientsRef.current.delete(patientID);
    patientOrderTsRef.current.delete(patientID);
    // console.log(`✅ Patient ${patientID} fully cleaned`);

    setTimeout(() => {
      setCleaningPatients((prev) => {
        const next = new Set(prev);
        next.delete(patientID);
        return next;
      });
    }, 100);
  };

  const openAlerts = (e) => {
    if (!e) return;

    setAlertsAnchorEl(e.currentTarget);

    setActiveAlerts((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((id) => {
        updated[id] = { ...updated[id], seen: true };
      });
      return updated;
    });
  };

  const handleMute = () => {
    // If it currently appears muted (either explicitly or due to lock), we unmute and unlock
    if (isMuted || !isAudioUnlocked) {
      if (!AlertAudio.isUnlocked) {
        AlertAudio.unlock();
        setIsAudioUnlocked(true);
      }
      setIsMuted(false);
      justUnmutedRef.current = true;
    } else {
      // Otherwise, we mute it
      setIsMuted(true);
    }
  };

  const alertPriorityMap = Object.values(activeAlerts).reduce((acc, alert) => {
    acc[alert.patientID] = alert.timestamp;
    return acc;
  }, {});

  const orderedData = React.useMemo(() => {
    const alertPatients = [];
    const normalPatients = [];

    data.forEach((patient) => {
      if (activeSortingAlertsRef.current.has(patient.patientID)) {
        alertPatients.push(patient);
      } else {
        normalPatients.push(patient);
      }
    });

    alertPatients.sort(
      (a, b) =>
        (activeSortingAlertsRef.current.get(b.patientID) || 0) -
        (activeSortingAlertsRef.current.get(a.patientID) || 0),
    );

    normalPatients.sort((a, b) => {
      const aTs =
        patientOrderTsRef.current.get(a.patientID) ??
        patientFirstSeenRef.current.get(a.patientID) ??
        0;

      const bTs =
        patientOrderTsRef.current.get(b.patientID) ??
        patientFirstSeenRef.current.get(b.patientID) ??
        0;

      return bTs - aTs;
    });

    // ⚠️ ENFORCE 12-PATIENT LICENSE LIMIT
    // Show alert patients first (critical), then normal patients
    // But never exceed MAX_PATIENTS_ALLOWED total
    const combined = [...alertPatients, ...normalPatients];
    return combined.slice(0, MAX_PATIENTS_ALLOWED);
  }, [data, activeAlerts]);

  // Calculate grid layout based on CAPPED orderedData (max 12 patients)
  const columns = orderedData.length > 9 ? 4 : 3;
  const MIN_SLOTS = columns === 4 ? 12 : 9;
  const TOTAL_SLOTS = Math.max(orderedData.length, MIN_SLOTS);
  const slots = Array.from({ length: TOTAL_SLOTS });

  useEffect(() => {
    AlertAudio.init();

    const onUnlockEvent = () => {
      setIsAudioUnlocked(true);
      // Keeps this tab exempt from background throttling/freezing for as
      // long as the monitor stays open — see startKeepAlive's comment.
      AlertAudio.startKeepAlive();
    };
    const onLockEvent = () => setIsAudioUnlocked(false);

    window.addEventListener("audioUnlocked", onUnlockEvent);
    window.addEventListener("audioLocked", onLockEvent);

    if (AlertAudio.isUnlocked) AlertAudio.startKeepAlive();

    return () => {
      window.removeEventListener("audioUnlocked", onUnlockEvent);
      window.removeEventListener("audioLocked", onLockEvent);
      AlertAudio.stopKeepAlive();
    };
  }, []);

  // useEffect(() => {
  //   const unlock = (e) => {
  //     if (e && e.target && e.target.closest && e.target.closest(".mute-btn"))
  //       return;

  //     AlertAudio.unlock();
  //     setIsAudioUnlocked(true);
  //   };
  //   window.addEventListener("click", unlock, { capture: true });
  //   window.addEventListener("touchstart", unlock, { capture: true });

  //   return () => {
  //     window.removeEventListener("click", unlock, { capture: true });
  //     window.removeEventListener("touchstart", unlock, { capture: true });
  //   };
  // }, []);

  useEffect(() => {
    AlertAudio.setMuted(isMuted);
  }, [isMuted]);

  useEffect(() => {
    if (isMuted || !isAudioUnlocked) return;

    const activeAlertPatientIDs = Array.from(
      activeSortingAlertsRef.current.keys(),
    );

    const alertCandidates = activeAlertPatientIDs
      .map((pid) => activeAlerts[pid])
      .filter((a) => {
        const isValid =
          a && Array.isArray(a.alerts) && a.alerts.length > 0 && !a.seen;
        if (a && !isValid) {
        }
        return isValid;
      });

    if (alertCandidates.length === 0) return;

    let latestGlobalTs = 0;
    let representativePatientID = "";

    alertCandidates.forEach((candidate) => {
      const timestamps = candidate.alerts.map((x) => Number(x.timestamp) || 0);
      const latestTs = Math.max(...timestamps);
      if (latestTs > latestGlobalTs) {
        latestGlobalTs = latestTs;
        representativePatientID = candidate.patientID;
      }
    });

    if (latestGlobalTs) {
      AlertAudio.playAlert(latestGlobalTs, representativePatientID);
    }
  }, [activeAlerts, isMuted, isAudioUnlocked]);

  useEffect(() => {
    const cleanupStaleStorage = () => {
      try {
        const now = Date.now();
        const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
        const keysToRemove = [];

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (
            key &&
            (key.startsWith("patient_hard_cleaned_") ||
              key.startsWith("patient_first_seen_") ||
              key.startsWith("cms_no_data_since_"))
          ) {
            const value = localStorage.getItem(key);
            if (value && now - Number(value) > TWENTY_FOUR_HOURS_MS) {
              keysToRemove.push(key);
            }
          }
        }

        keysToRemove.forEach((k) => localStorage.removeItem(k));
        if (keysToRemove.length > 0) {
          console.log(
            `[STORAGE CLEANUP] Cleared ${keysToRemove.length} stale localStorage flags`,
          );
        }
      } catch (e) {
        console.warn("[STORAGE CLEANUP] Error during localStorage cleanup:", e);
      }
    };

    cleanupOldIndexedDB();
    cleanupStaleStorage();

    const interval = setInterval(
      () => {
        cleanupOldIndexedDB();
        cleanupStaleStorage();
      },
      10 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(ALERTS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object") {
          const ONE_HOUR_MS = 1 * 60 * 60 * 1000;
          const now = Date.now();
          const cleaned = {};

          Object.entries(parsed).forEach(([patientID, alertData]) => {
            const latestTs = alertData?.alerts?.length
              ? Math.max(...alertData.alerts.map((a) => a.timestamp))
              : null;

            if (latestTs && now - latestTs <= ONE_HOUR_MS) {
              cleaned[patientID] = alertData;
            }
          });

          setActiveAlerts(cleaned);
        }
      }
    } catch (e) {
      console.warn("Failed to restore alerts from storage", e);
    } finally {
      setAlertsHydrated(true);
      setSkipPersistOnce(true);
    }
  }, []);

  useEffect(() => {
    if (cookieData) {
      const parsedData = JSON.parse(cookieData);
      // const accessToken = cookieValue.access_token;
      //const algorithm = parsedData.fetal_distress_algorithm;
      const account_type = parsedData.account_type;
      setAccountType(account_type);
    }
  }, [cookieData]);

  // AFTER
  // useCallback for the same reason as handleAlertChange above — a stable
  // reference lets GraphCard's memoization work.
  const openSingleCard = useCallback((patientID) => {
    const strId = String(patientID);
    setActivePatientID(strId);
    try {
      sessionStorage.setItem("cms_active_patient_id", strId);
    } catch {}
  }, []);

  const backToGrid = () => {
    setActivePatientID(null);
    try {
      sessionStorage.removeItem("cms_active_patient_id");
    } catch {}
  };

  const patientNotFoundTimerRef = useRef(null);

  // If single view was restored from sessionStorage but patient doesn't appear → fall back to grid
  useEffect(() => {
    if (!activePatientID) {
      if (patientNotFoundTimerRef.current) {
        clearTimeout(patientNotFoundTimerRef.current);
        patientNotFoundTimerRef.current = null;
      }
      return;
    }

    const found = data.find(
      (p) => String(p.patientID) === String(activePatientID),
    );
    if (found) {
      // Patient found — cancel any pending fallback
      if (patientNotFoundTimerRef.current) {
        clearTimeout(patientNotFoundTimerRef.current);
        patientNotFoundTimerRef.current = null;
      }
    } else if (!patientNotFoundTimerRef.current) {
      // Patient not (or no longer) present. Empty data after we've already
      // received data means the last session ended: leave quickly. Empty
      // data before any has arrived is just a reload still connecting, so
      // wait longer for it.
      const everHadData = data.length > 0 || hasReceivedFirstDataRef.current;
      patientNotFoundTimerRef.current = setTimeout(
        () => {
          setActivePatientID(null);
          try {
            sessionStorage.removeItem("cms_active_patient_id");
          } catch {}
          patientNotFoundTimerRef.current = null;
        },
        everHadData ? (data.length > 0 ? 10000 : 3000) : 30000,
      );
    }
  }, [data, activePatientID]);

  const toggleTheme = () => {
    const newTheme = theme === "light-theme" ? "dark-theme" : "light-theme";

    setTheme(newTheme);

    Cookie.set("theme", newTheme, {
      expires: 365,
      path: "/",
    });
  };

  useEffect(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem(ALERTS_STORAGE_KEY) || "{}",
      );

      let changed = false;
      const ONE_HOUR_MS = 1 * 60 * 60 * 1000;
      const now = Date.now();

      Object.entries(stored).forEach(([patientID, alert]) => {
        const latestTs = alert?.alerts?.length
          ? Math.max(...alert.alerts.map((a) => a.timestamp))
          : null;

        if (!latestTs || now - latestTs > ONE_HOUR_MS) {
          delete stored[patientID];
          changed = true;
        }
      });

      if (changed) {
        localStorage.setItem(ALERTS_STORAGE_KEY, JSON.stringify(stored));
        console.log("[ALERT CLEANUP] Removed old alerts from localStorage");
      }
    } catch (e) {
      console.warn("Deferred alert cleanup failed", e);
    }
  }, []);

  useEffect(() => {
    if (!isMuted) {
      const allAlerts = Object.values(activeAlerts).flatMap(
        (a) => a.alerts || [],
      );

      if (allAlerts.length > 0) {
        lastPlayedAlertTsRef.current = Math.max(
          ...allAlerts.map((a) => a.timestamp),
        );
      }
    }
  }, [isMuted]);

  useEffect(() => {
    if (!alertsHydrated) return;

    if (skipPersistOnce) {
      setSkipPersistOnce(false);
      return;
    }

    try {
      localStorage.setItem(ALERTS_STORAGE_KEY, JSON.stringify(activeAlerts));
    } catch (e) {
      console.warn("Failed to persist alerts", e);
    }
  }, [activeAlerts, alertsHydrated, skipPersistOnce]);

  useEffect(() => {
    if (
      alertsHydrated &&
      hasReceivedFirstDataRef.current &&
      data.length === 0 &&
      !monitoringActiveRef.current &&
      missingPatientsRef.current.size === 0
    ) {
      console.log("ALERT CLEANUP — confirmed no patients after data load");

      setActiveAlerts({});
      localStorage.removeItem(ALERTS_STORAGE_KEY);
    }
  }, [alertsHydrated, data.length]);

  //uncomment it after mock

  // useEffect(() => {
  //   // 1️⃣ Sort incoming patients
  //   const temp_keyar_data = [...keyarData].sort((a, b) => {
  //     const aTime = a.session_time || 0;
  //     const bTime = b.session_time || 0;
  //     if (bTime !== aTime) return bTime - aTime;

  //     const aId = isNaN(Number(a.patientID))
  //       ? String(a.patientID)
  //       : Number(a.patientID);
  //     const bId = isNaN(Number(b.patientID))
  //       ? String(b.patientID)
  //       : Number(b.patientID);

  //     if (typeof aId === "number" && typeof bId === "number") return aId - bId;
  //     return String(aId).localeCompare(String(bId));
  //   });

  //   // 2️⃣ Filter valid patients
  //   const filteredPatients = temp_keyar_data.filter(
  //     (p) => p.keyarData && Array.isArray(p.keyarData) && p.keyarData.length > 0
  //   );

  //   let finalPatients = filteredPatients;

  //   const isLocal = window.location.hostname === "localhost";
  //   const TARGET_COUNT = 14;

  //   if (isLocal && filteredPatients.length < TARGET_COUNT) {
  //     const base = filteredPatients[0];
  //     if (base) {
  //       const baseSession = base.session_time || Math.floor(Date.now() / 1000);
  //       const mocksNeeded = TARGET_COUNT - filteredPatients.length;

  //       const mocks = Array.from({ length: mocksNeeded }, (_, i) => ({
  //         ...base,
  //         patientID: `${base.patientID}-MOCK-${i + 1}`,
  //         patientName: `${base.patientName} MOCK ${i + 1}`,
  //         session_time: baseSession + i + 1, // 🔑 unique & stable
  //       }));

  //       finalPatients = [...filteredPatients, ...mocks];
  //     }
  //   }

  //   console.log(
  //     "FINAL PATIENTS:",
  //     finalPatients.map((p) => p.patientID)
  //   );

  //   // 4️⃣ Stable reference optimization (do NOT remove)
  //   const stablePatients = finalPatients.map((patient) => {
  //     const key = `${patient.patientID}_${patient.session_time}`;

  //     if (patientMapRef.current.has(key)) {
  //       const existing = patientMapRef.current.get(key);

  //       // If device data changed → create new object
  //       if (existing.keyarData !== patient.keyarData) {
  //         const updated = {
  //           patientID: patient.patientID,
  //           patientName: patient.patientName,
  //           keyarData: patient.keyarData,
  //           session_time: patient.session_time,
  //         };
  //         patientMapRef.current.set(key, updated);
  //         return updated;
  //       }

  //       return existing; // reuse old reference
  //     }

  //     // New patient → store fresh clone
  //     const created = {
  //       patientID: patient.patientID,
  //       patientName: patient.patientName,
  //       keyarData: patient.keyarData,
  //       session_time: patient.session_time,
  //     };

  //     patientMapRef.current.set(key, created);
  //     return created;
  //   });

  //   // 5️⃣ Cleanup removed patients
  //   const currentKeys = new Set(
  //     stablePatients.map((p) => `${p.patientID}_${p.session_time}`)
  //   );
  //   for (const [key] of patientMapRef.current) {
  //     if (!currentKeys.has(key)) {
  //       patientMapRef.current.delete(key);
  //     }
  //   }

  //   setData(stablePatients);

  //   setError(finalPatients.length === 0);
  // }, [keyarData]);

  //original code

  useEffect(() => {
    if (Array.isArray(keyarData) && keyarData.length > 0) {
      hasReceivedFirstDataRef.current = true;
    }
    // Stable card identity per session. Entries without a session_uuid
    // (REST-sourced patients) are untouched: identity stays their patientID.
    // assignedPatientId is the *current* patient ID, used for patient-detail
    // lookups; it is a real number only once a patient has been assigned.
    const withStableId = (p) => {
      if (!p.session_uuid) return { ...p, assignedPatientId: p.patientID };
      let stableId = sessionKeyRef.current.get(p.session_uuid);
      if (stableId === undefined) {
        stableId = p.patientID;
        sessionKeyRef.current.set(p.session_uuid, stableId);
      }
      return { ...p, patientID: stableId, assignedPatientId: p.patientID };
    };
    const temp_keyar_data = [...keyarData].map(withStableId);

    temp_keyar_data.forEach((p) => {
      if (!patientFirstSeenRef.current.has(p.patientID)) {
        const stored = localStorage.getItem(
          `patient_first_seen_${p.patientID}`,
        );
        if (stored) {
          patientFirstSeenRef.current.set(p.patientID, Number(stored));
        }
      }
    });

    temp_keyar_data.forEach((p) => {
      if (!patientFirstSeenRef.current.has(p.patientID)) {
        const now = Date.now();

        patientFirstSeenRef.current.set(p.patientID, now);
        localStorage.setItem(
          `patient_first_seen_${p.patientID}`,
          now.toString(),
        );
      }

      patientLastSeenRef.current.set(p.patientID, Date.now());
    });

    // ✅ Track when each patient was first seen
    const incomingPatientIDs = new Set(temp_keyar_data.map((p) => p.patientID));
    // temp_keyar_data.forEach((p) => {
    //   if (!patientFirstSeenRef.current.has(p.patientID)) {
    //     patientFirstSeenRef.current.set(p.patientID, Date.now());
    //     // console.log(`[${p.patientID}] 🆕 First seen at ${new Date().toISOString()}`);
    //   }
    //   patientLastSeenRef.current.set(p.patientID, Date.now());
    // });

    temp_keyar_data.sort((a, b) => {
      const aSession = a.session_time || 0;
      const bSession = b.session_time || 0;

      if (bSession !== aSession) {
        return bSession - aSession;
      }

      const aSeen = patientFirstSeenRef.current.get(a.patientID) || 0;
      const bSeen = patientFirstSeenRef.current.get(b.patientID) || 0;

      if (bSeen !== aSeen) {
        return bSeen - aSeen;
      }

      return String(a.patientID).localeCompare(String(b.patientID));
    });

    const validLivePatientIDs = new Set(
      temp_keyar_data.map((p) => String(p.patientID)),
    );

    const nowMillis = Date.now();
    const GRACE_PERIOD_MS = 10000;

    const currentCardKeys = Array.from(patientMapRef.current.keys());
    const alertKeys = Object.keys(activeAlerts);
    const allKnownIDs = new Set([...currentCardKeys, ...alertKeys]);

    allKnownIDs.forEach((id) => {
      const pid = String(id);
      if (!validLivePatientIDs.has(pid)) {
        if (!missingPatientsRef.current.has(pid)) {
          missingPatientsRef.current.set(pid, nowMillis);
        }
      } else {
        missingPatientsRef.current.delete(pid);
      }
    });

    const patientsToDelete = [];
    missingPatientsRef.current.forEach((firstSeenMissing, pid) => {
      if (nowMillis - firstSeenMissing >= GRACE_PERIOD_MS) {
        patientsToDelete.push(pid);
      }
    });

    if (patientsToDelete.length > 0) {
      // console.log(`🧹 Grace period reached for: ${patientsToDelete}. Cleaning up.`);

      setActiveAlerts((prev) => {
        const next = { ...prev };
        let changed = false;
        patientsToDelete.forEach((pid) => {
          if (next[pid]) {
            delete next[pid];
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      patientsToDelete.forEach((pid) => {
        cleanupPatientCompletely(pid);
        missingPatientsRef.current.delete(pid);
      });
    }

    const STALE_DATA_THRESHOLD_MS = 60 * 60 * 1000;
    const filteredPatients = temp_keyar_data.filter((patient) => {
      // ✅ Improved filter: Check for actual data points across all params, not just empty objects
      let latestGlobalTimestamp = 0;
      let hasActualDataPoints = false;

      if (Array.isArray(patient.keyarData)) {
        patient.keyarData.forEach((p) => {
          const entries = Object.keys(p.latest_data || {});
          if (entries.length > 0) {
            hasActualDataPoints = true;
            const pMax = Math.max(
              ...entries.map((ts) =>
                String(ts).length === 13 ? Number(ts) : Number(ts) * 1000,
              ),
            );
            if (pMax > latestGlobalTimestamp) latestGlobalTimestamp = pMax;
          }
        });
      }

      const now = Date.now();

      // 1️⃣ Case: Patient has actual data points -> Check for staleness
      if (hasActualDataPoints && latestGlobalTimestamp > 0) {
        const dataAge = now - latestGlobalTimestamp;

        // Clear persistence markers if real data arrives
        localStorage.removeItem(`cms_no_data_since_${patient.patientID}`);
        patientNoDataSinceRef.current.delete(patient.patientID);

        if (dataAge > STALE_DATA_THRESHOLD_MS) {
          return false;
        }
        return true;
      }

      // 2️⃣ Case: No data points yet (or empty) -> Grant a grace period if they're in the API
      const stillIncoming = incomingPatientIDs.has(patient.patientID);
      if (stillIncoming) {
        let lastNoData = patientNoDataSinceRef.current.get(patient.patientID);

        // HYDRATION: Check localStorage if Ref is empty (post-refresh)
        if (!lastNoData) {
          const stored = localStorage.getItem(
            `cms_no_data_since_${patient.patientID}`,
          );
          if (stored) {
            lastNoData = Number(stored);
            patientNoDataSinceRef.current.set(patient.patientID, lastNoData);
          }
        }

        if (!lastNoData) {
          patientNoDataSinceRef.current.set(patient.patientID, now);
          localStorage.setItem(
            `cms_no_data_since_${patient.patientID}`,
            String(now),
          );
          return true; // Show for first 30s
        }

        if (now - lastNoData < 30_000) {
          return true; // Still within 30s grace period
        }

        // Hide permanently (stays in localStorage/Ref)
        return false;
      }

      // 3️⃣ Case: Patient not in API at all -> Clean up markers
      localStorage.removeItem(`cms_no_data_since_${patient.patientID}`);
      patientNoDataSinceRef.current.delete(patient.patientID);
      return false;
    });

    const stablePatients = filteredPatients
      .map((patient) => {
        const key = patient.patientID;

        if (hardCleanedPatientsRef.current.has(key)) {
          console.log(
            `[SESSION RESET] Patient ${key} was hard-cleaned. Removing old session lock`,
          );

          console.log(
            `[SESSION RESET] Old locked session =`,
            patientSessionsRef.current.get(key),
          );

          patientSessionsRef.current.delete(key);
          hardCleanedPatientsRef.current.delete(key);
        }

        if (
          patient.session_time &&
          patientSessionsRef.current.get(key) !== patient.session_time
        ) {
          // A different session_time for a patient we already have a
          // locked value for means backend started a new session for them
          // without ever disappearing from the feed long enough to trigger
          // the hard-clean reset below — adopt it so the lock doesn't pin
          // this card (and chartLayout2.js downstream) to a stale session
          // forever.
          patientSessionsRef.current.set(key, patient.session_time);
        }

        let lockedSessionTime = patient.session_time;

        if (!hardCleanedPatientsRef.current.has(key)) {
          lockedSessionTime =
            patientSessionsRef.current.get(key) || patient.session_time;
        } else {
          patientSessionsRef.current.delete(key);
        }

        if (patientMapRef.current.has(key)) {
          const existingPatient = patientMapRef.current.get(key);

          if (!patient.keyarData || patient.keyarData.length === 0) {
            // console.log(
            //   `[${patient.patientID}] No new data from backend, keeping cached data`
            // );
            return existingPatient;
          }

          if (existingPatient.assignedPatientId !== patient.assignedPatientId) {
            // Patient got assigned mid-session: same card, new details ID.
            const reassigned = {
              ...existingPatient,
              assignedPatientId: patient.assignedPatientId,
              keyarData: patient.keyarData?.length
                ? patient.keyarData
                : existingPatient.keyarData,
              session_time: lockedSessionTime,
            };
            patientMapRef.current.set(key, reassigned);
            return reassigned;
          }

          if (existingPatient.keyarData !== patient.keyarData) {
            const hospital = childPlaces.find(
              (h) => String(h.id) === String(patient.place_id)
            );

            const updatedPatient = {
              patientID: patient.patientID,
              assignedPatientId: patient.assignedPatientId,
              patientName: patient.patientName,
              keyarData: patient.keyarData,
              session_time: lockedSessionTime,
              session_uuid: patient.session_uuid,
             hospitalName: hospital?.name || "",
  hospitalLogo: hospital?.logo || null,
            };
            patientMapRef.current.set(key, updatedPatient);
            return updatedPatient;
          }

          return existingPatient;
        } else {
          if (!patient.keyarData || patient.keyarData.length === 0) {
            return null;
          }

const hospital = childPlaces.find(
  (h) => String(h.id) === String(patient.place_id)
);

          const newPatient = {
            patientID: patient.patientID,
            assignedPatientId: patient.assignedPatientId,
            patientName: patient.patientName,
            keyarData: patient.keyarData,
            session_time: lockedSessionTime,
            session_uuid: patient.session_uuid,
            hospitalName: hospital?.name || "",
            hospitalLogo: hospital?.logo || null,
          };
          patientMapRef.current.set(key, newPatient);
          try {
            patientOrderTsRef.current.set(key, Date.now());
          } catch (e) {}
          return newPatient;
        }
      })
      .filter(Boolean);

    const nowTs = Date.now();
    const currentPatientIDs = new Set(stablePatients.map((p) => p.patientID));

    prevPatientIDsRef.current = currentPatientIDs;

    if (stablePatients.length > 0) {
      monitoringActiveRef.current = true;
    }

    if (stablePatients.length > 0) {
      lastNonEmptyDataRef.current = stablePatients;
      noPatientsSinceRef.current = null;
      setData(stablePatients);
      setError(false);
    } else {
      setData([]);
      setError(true);
    }

    const prevCount = prevStablePatientCountRef.current;
    const currentCount = stablePatients.length;

    prevStablePatientCountRef.current = currentCount;

    if (prevCount > 0 && currentCount === 0) {
      hasReceivedFirstDataRef.current = true;
    }

    // if (monitoringActiveRef.current && prevCount > 0 && currentCount === 0) {
    //   monitoringActiveRef.current = false;

    //   setActiveAlerts({});
    //   localStorage.removeItem(ALERTS_STORAGE_KEY);
    //   cleanupOldIndexedDB();

    //   console.log("ALERT CLEANUP — monitoring truly ended");
    // }
  }, [keyarData]);

  const logout = async () => {
    setLoading(true);
    await performLogout(navigate);
    setLoading(false);
  };

  const back = () => {
    if (activePatientID) {
      setActivePatientID(null);
      try {
        sessionStorage.removeItem("cms_active_patient_id");
      } catch {}
    } else {
      navigate("/dashboard/report");
    }
  };

  // console.log("CentralGraphLayout Debug:", {
  //   dataLength: data.length,
  //   data: data,
  //   error: error,
  // });
  const alertList = Object.values(activeAlerts).sort((a, b) => {
    if (a.seen !== b.seen) return a.seen ? 1 : -1;

    const aLatestTs = Math.max(...(a.alerts?.map((al) => al.timestamp) || [0]));

    const bLatestTs = Math.max(...(b.alerts?.map((al) => al.timestamp) || [0]));

    return bLatestTs - aLatestTs;
  });

  return (
    <>
      {loading ? (
        <div style={{ marginTop: "20%", position: "relative" }}>
          <Loader />
        </div>
      ) : (
        <div className={`central-graph-root ${theme}`}>
          <Modal
            open={!isAudioUnlocked && data.length > 0}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              if (reason === "escapeKeyDown") return;
            }}
            disableEscapeKeyDown
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            BackdropProps={{
              sx: {
                backgroundColor: "rgba(25, 25, 25, 0.8)",
                backdropFilter: "blur(0.1px)",
              },
            }}
          >
            <div className={`audio-lock-modal ${theme}`}>
              <div className="modal-icon">
                <img
                  src="/alerts_cms.svg"
                  alt="alerts"
                  className="alerts-modal-icon"
                />
              </div>

              <h2>Turn On Emergency Sound Alerts</h2>
              <p>
                To receive real-time emergency sound alerts for abnormal
                readings,please enable notification
              </p>

              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    AlertAudio.unlock();
                    setIsAudioUnlocked(true);
                    setIsMuted(true);
                  }}
                >
                  Mute
                </button>

                <button
                  className="enable-btn"
                  onClick={() => {
                    AlertAudio.unlock();
                    setIsAudioUnlocked(true);
                    setIsMuted(false);
                  }}
                >
                  Enable
                </button>
              </div>
            </div>
          </Modal>

          <div className="main">
            <div className="header">
              <div onClick={back}>
                <BackButton />
              </div>

              <div className="real-time-text">
                <div
                  className="cms-text-wrapper"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <img
                    src="/janitri_logo_cms.png"
                    alt="CMS Logo"
                    className="cms_logo"
                  />

                  <p className="real-time-monitoring">
                    Central Monitoring System
                  </p>
                </div>

                {(isSingleHospital || isSingleView) && (
                  <div className="hospital-container-wrapper">
                    {/* ✅ Logo */}
                    {(isSingleHospital
                      ? singleHospitalLogo
                      : activePatient?.hospitalLogo) && (
                      <img
                        src={
                          isSingleHospital
                            ? singleHospitalLogo
                            : activePatient?.hospitalLogo
                        }
                        alt="Hospital Logo"
                        className="hospital_logo"
                      />
                    )}

                    {/* ✅ Name */}
                    <p className="hospital-header-name">
                      {isSingleHospital
                        ? singleHospitalName
                        : activePatient?.hospitalName || ""}
                    </p>
                  </div>
                )}
              </div>

              <div className="header-actions">
                <div>
                  <button
                    className={`mute-btn ${isMuted ? "muted" : "unmuted"}`}
                    onClick={handleMute}
                    title={
                      isMuted || !isAudioUnlocked
                        ? "Unmute alerts"
                        : "Mute alerts"
                    }
                  >
                    <span>{isMuted ? "Mute" : "Sound"}</span>
                    <img
                      src={isMuted ? "/no_sound.svg" : "/brand_awareness.svg"}
                      alt={isMuted ? "Muted" : "Sound on"}
                    />
                  </button>
                </div>
                <div
                  className={`cms-theme-toggle ${theme}`}
                  onClick={toggleTheme}
                  title="Toggle CMS Theme"
                >
                  {theme === "light-theme" ? (
                    <>
                      <div className="theme-icon-circle">
                        <img src="/light_mode.svg" alt="Light mode" />
                      </div>
                      <span className="theme-text">Light Mode</span>
                    </>
                  ) : (
                    <>
                      <span className="theme-text">Dark Mode</span>
                      <div className="theme-icon-circle">
                        <img src="/dark_mode.svg" alt="Dark mode" />
                      </div>
                    </>
                  )}
                </div>
                <div className="alerts" style={{ marginRight: "20px" }}>
                  <Alerts_Cms
                    alertCount={
                      Object.values(activeAlerts).filter((a) => !a.seen).length
                    }
                    onClick={openAlerts}
                  />

                  <AlertsModal
                    anchorEl={alertsAnchorEl}
                    open={Boolean(alertsAnchorEl)}
                    onClose={() => setAlertsAnchorEl(null)}
                    alerts={alertList}
                    theme={theme}
                    activePatientIDs={data.map((p) => p.patientID)}
                  />
                </div>
              </div>
            </div>

            {/* Single view restored on reload: until THAT patient's data arrives
                (other patients may already be in `data`) show the loader, not an
                empty screen. If they never show up the timer above falls back
                to the grid. */}
            {activePatientID && !activePatient ? (
              <div style={{ marginTop: "20%", position: "relative" }}>
                <Loader />
              </div>
            ) : (!data || data.length === 0) && error ? (
              <CentralMonitorError />
            ) : isResyncing ? (
              <div style={{ marginTop: "20%", position: "relative" }}>
                <Loader />
              </div>
            ) : (
              <div
                className={
                  activePatientID
                    ? "single-graph-wrapper"
                    : `graph-grid ${data.length > 9 ? "grid-4x3" : "grid-3x3"}`
                }
              >
                {slots.map((_, index) => {
                  const patient = orderedData[index];

                  if (patient) {
                    const isActive =
                      String(patient.patientID) === String(activePatientID);
                    const isSingleMode = !!activePatientID;
                    const isVisible = isSingleMode ? isActive : true;

                    return (
                      <div
                        key={patient.patientID}
                        className={
                          isSingleMode
                            ? "single-view-item"
                            : "grid-card-wrapper"
                        }
                        style={{
                          display: isVisible ? "block" : "none",
                          width: isSingleMode ? "100%" : "auto",
                          height: isSingleMode ? "100%" : "auto",
                        }}
                      >
                        <GraphCard
                          patientID={patient.patientID}
                          isBeingCleaned={cleaningPatients.has(
                            patient.patientID,
                          )}
                          patientName={patient.patientName}
                          keyarData={patient.keyarData}
                          session_time={patient.session_time}
                          session_uuid={patient.session_uuid}
                          assignedPatientId={patient.assignedPatientId}
                          index={index}
                          count={data.length}
                          isSingleView={isActive}
                          onOpenSingleCard={openSingleCard}
                          onAlertChange={handleAlertChange}
                          // single-facility accounts already see the hospital in
                          // the header; names on cards are for multi-hospital ones
                          hospitalName={
                            isSingleHospital
                              ? ""
                              : isActive
                                ? activePatient?.hospitalName
                                : patient.hospitalName
                          }
                          hospitalLogo={
                            isActive
                              ? activePatient?.hospitalLogo
                              : patient.hospitalLogo
                          }
                        />
                      </div>
                    );
                  }

                  if (!activePatientID) {
                    return (
                      <div
                        key={`placeholder-${index}`}
                        className="graph-card-placeholder"
                      >
                        <div className="placeholder-inner">
                          <div className="placeholder-box">
                            <div className="placeholder-content">
                              <div className="slot-number">
                                Slot {index + 1}
                              </div>
                              <img
                                src="/mynaui_monitor.svg"
                                alt="No Live NST"
                                className="placeholder-icon-img"
                              />
                              <div className="placeholder-text">
                                No Live Monitoring in Progress
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            )}

            {!activePatientID && (!data || data.length === 0) && !error && (
              <div />
            )}
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  keyarData: selectMergedKeyarData(state),
});

// ⚠️ PERFORMANCE FIX: Wrap with React.memo to prevent re-renders when
// keyarData hasn't actually changed (even if Redux ran)
const MemoizedCentralGraphLayout = React.memo(CentralGraphLayout, (prevProps, nextProps) => {
  if (prevProps.isResyncing !== nextProps.isResyncing) return false;

  // Only re-render if keyarData array length or patient IDs changed
  if (prevProps.keyarData.length !== nextProps.keyarData.length) return false;

  // Check if patient IDs or lastFrameAt changed (indicates new data)
  const prevIDs = prevProps.keyarData.map(p => `${p.patientID}-${p.lastFrameAt}`).join(',');
  const nextIDs = nextProps.keyarData.map(p => `${p.patientID}-${p.lastFrameAt}`).join(',');
  
  return prevIDs === nextIDs; // true = skip re-render
});

export default connect(mapStateToProps)(MemoizedCentralGraphLayout);
